import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled from "@emotion/styled";

const Root = styled.section`
  background: #fafafa;
  padding: 40px 20px;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const Title = styled.h2`
  color: #2b2b2b;
  font-size: 30px;
  font-weight: 700;
  margin-right: 20px;
  margin-bottom: 20px;
  width: 100%;

  @media (min-width: 768px) {
    font-size: 45px;
    margin-right: 20px;
    width: 30%;
  }
`;

const AccordionContainer = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    width: 70%;
  }
`;

const AccordionItem = styled.div`
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const AccordionHeader = styled.div`
  background: #f0f0f0;
  padding: 16px;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AccordionContent = styled.div`
  padding: 16px;
  display: ${props => (props.expanded ? "block" : "none")};
`;

const FaqTitle = styled.h3`
  font-weight: 600;
  font-size: 18px;
  margin: 0;
`;

const FaqContent = styled.p`
  font-size: 16px;
  line-height: 1.5;
`;

const AccordionExpandDefault = () => {
  const [expanded, setExpanded] = useState(null);
  const isLang = useSelector((state) => state.authReducer.lang);

  const faqs = [
    {
      title: isLang === "eng" ? "What is Maker4U and what do you offer?" : "ما هو Maker4U وماذا تقدم؟",
      content: isLang === "eng"
        ? "Maker4U is a platform that provides a diverse range of templates for various purposes. Whether you need templates for websites, presentations, documents, or other projects, we've got you covered."
        : "Maker4U هو منصة تقدم مجموعة متنوعة من القوالب لأغراض مختلفة. سواء كنت بحاجة إلى قوالب للمواقع أو العروض التقديمية أو المستندات أو المشاريع الأخرى، نحن نغطي كل شيء.",
    },
    {
      title: isLang === "eng" ? "How do I browse and find templates on Maker4U?" : "كيف أستعرض وأجد القوالب على Maker4U؟",
      content: isLang === "eng"
        ? "Navigating our website is easy. Simply browse through the categories or use our search feature to find the templates that suit your needs. Once you find a template you like, you can preview it before downloading."
        : "التنقل في موقعنا سهل للغاية. ما عليك سوى استعراض الفئات أو استخدام ميزة البحث للعثور على القوالب التي تناسب احتياجاتك. بمجرد العثور على قالب يعجبك، يمكنك معاينته قبل التنزيل.",
    },
    {
      title: isLang === "eng" ? "Are the templates on Maker4U free to use?" : "هل القوالب على Maker4U مجانية الاستخدام؟",
      content: isLang === "eng"
        ? "We offer a mix of free and premium templates. Free templates can be used without any charge, while premium templates may require a one-time purchase. Check the pricing details on each template's page for more information."
        : "نقدم مزيجًا من القوالب المجانية والمتميزة. يمكن استخدام القوالب المجانية دون أي رسوم، بينما قد تتطلب القوالب المتميزة شراء مرة واحدة. تحقق من تفاصيل التسعير على صفحة كل قالب لمزيد من المعلومات.",
    },
    {
      title: isLang === "eng" ? "Can I customize the templates to fit my specific requirements?" : "هل يمكنني تخصيص القوالب لتناسب متطلباتي الخاصة؟",
      content: isLang === "eng"
        ? "Absolutely! Our templates are designed to be easily customizable. You can edit text, images, colors, and other elements to tailor the template to your unique needs using compatible software."
        : "بالطبع! تم تصميم قوالبنا بحيث تكون قابلة للتخصيص بسهولة. يمكنك تعديل النصوص والصور والألوان والعناصر الأخرى لتلائم احتياجاتك الخاصة باستخدام البرامج المتوافقة.",
    },
    {
      title: isLang === "eng" ? "How do I download a template from Maker4U?" : "كيف أقوم بتنزيل قالب من Maker4U؟",
      content: isLang === "eng"
        ? "To download a template, simply click on the 'Download' button on the template's page. If it's a free template, you may be able to download it directly. For premium templates, follow the checkout process to access the download link."
        : "لتنزيل قالب، ما عليك سوى النقر على زر 'تنزيل' في صفحة القالب. إذا كان القالب مجانيًا، يمكنك تنزيله مباشرة. بالنسبة للقوالب المتميزة، اتبع عملية الدفع للوصول إلى رابط التنزيل.",
    }
  ];

  const handleToggle = (index) => {
    setExpanded(expanded === index ? null : index);
  };

  return (
    <Root>
      <Container>
        <Title>
          {isLang === "eng" ? "Frequently Asked Questions" : "أسئلة مكررة"}
        </Title>
        <AccordionContainer>
          {faqs.map((faq, index) => (
            <AccordionItem key={index}>
              <AccordionHeader onClick={() => handleToggle(index)}>
                <FaqTitle>{faq.title}</FaqTitle>
                <span>{expanded === index ? '-' : '+'}</span>
              </AccordionHeader>
              <AccordionContent expanded={expanded === index}>
                <FaqContent>{faq.content}</FaqContent>
              </AccordionContent>
            </AccordionItem>
          ))}
        </AccordionContainer>
      </Container>
    </Root>
  );
};

export default AccordionExpandDefault;
